// Per-case quick run: the play button on a case creates a sphinx run for that
// single case (`caseIds: [id]`) against the clients enabled on Client
// Connectors, with the default Yarn env + org, and lands on the run detail
// view. When no client is enabled the caller opens the eval wizard in
// single-case mode instead (`onNeedTargets`) rather than guessing.
//
// Only client IDS go over the wire. Each client's models (`targets.models`)
// and connectors (`targets.server_ids`) are read server-side when the run is
// created, so a client with two models runs both — the request must not
// flatten that to one.
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { convexErrMessage } from "@/lib/convex-error";
import { buildEvalsPath, navigateApp } from "@/lib/app-navigation";
import { track } from "@/lib/analytics";
import { createSphinxRun } from "@/data/evals";
import { effectiveModels } from "@/lib/client-models";
import type { HostListItem } from "@/hooks/useClients";
import { DEFAULT_YARN_ORG, type TargetId } from "@/shared/sphinx";
import { DEFAULT_YARN_ENV } from "./eval-runner";

/**
 * The clients a run defaults to: every ENABLED one.
 *
 * It used to also require a singular `t.model`, which quietly dropped a client
 * configured with `models` alone — the very clients the multi-model picker
 * creates. What a client runs is the server's call (`modelsForTarget` in
 * `server/queue/enqueue.ts`: its `models` list, else `model`, else the
 * provider default), so enabled is the whole question here.
 */
export function enabledTargetIds(targets: HostListItem[]): TargetId[] {
  return targets.filter((t) => t.enabled).map((t) => t.hostId as TargetId);
}

/**
 * How many iterations a run will actually create: every enabled client is
 * expanded over each of its models, then over each case. Mirrors the server's
 * case × client × model fan-out (`createRunInTx`), so the wizard can say what
 * is about to happen instead of letting it surprise someone.
 */
export function countRunIterations(
  targets: HostListItem[],
  caseCount: number
): number {
  const perCase = targets.reduce(
    (total, target) => total + Math.max(1, effectiveModels(target).length),
    0
  );

  return perCase * caseCount;
}

export function useQuickRun({
  suiteId,
  targets,
  onNeedTargets,
}: {
  suiteId: string | null;
  targets: HostListItem[];
  /** Called with the case id when no enabled target exists (open the wizard). */
  onNeedTargets: (caseId: string) => void;
}) {
  const [runningCaseId, setRunningCaseId] = useState<string | null>(null);

  const runCase = useCallback(
    async (caseId: string): Promise<string | null> => {
      if (!suiteId || runningCaseId) return null;
      const targetIds = enabledTargetIds(targets);
      if (targetIds.length === 0) {
        onNeedTargets(caseId);
        return null;
      }
      setRunningCaseId(caseId);
      try {
        const run = await createSphinxRun(suiteId, {
          targets: targetIds,
          yarnEnv: DEFAULT_YARN_ENV,
          yarnOrg: DEFAULT_YARN_ORG,
          caseIds: [caseId],
        });
        track("eval_test_case_run_started", {
          location: "test_cases_overview",
          suite_id: suiteId,
          test_case_id: caseId,
          num_targets: targetIds.length,
        });
        navigateApp(buildEvalsPath({ type: "run-detail", suiteId, runId: run.id }));
        return run.id;
      } catch (error) {
        toast.error(convexErrMessage(error, "Failed to run test case"));
        return null;
      } finally {
        setRunningCaseId(null);
      }
    },
    [suiteId, runningCaseId, targets, onNeedTargets]
  );

  return { runCase, runningCaseId };
}
